import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useLocale } from 'next-intl';
import { SearchSuggestions } from '@/types/searchSuggestions';
import { get, toQueryString } from '@/utility/requestHelpers/clientRequestHelpers';
import { useDebounce } from './useDebounce';
const EMPTY_SUGGESTIONS: SearchSuggestions = {
  licenses: [],
  productTypes: [],
  tags: [],
  categoryCompletions: [],
  topics: []
};

/**
 * Licences, product types and tags for the header search, in one request. Tags are fetched and
 * blocklist-filtered on the server, which is also where they pick up their category link.
 *
 * `queryCategory` is the category the typed query itself resolves to. It has no row in the dropdown;
 * it is what a plain Enter uses, and it is the only path that works for a phrase the tag API has
 * never heard of. `debouncedSearchTerm` is returned alongside so the caller can check that a
 * submitted value really is the query this resolution belongs to.
 */
export const useSearchSuggestions = () => {
  const $ = _c(16);
  const locale = useLocale();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(null);
  const hasTypedSinceNavigation = useRef(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = value => {
      if (!hasTypedSinceNavigation.current) {
        return;
      }
      setDebouncedSearchTerm(value.toLowerCase());
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const setDebouncedSearchTermIfTyped = useDebounce(t0, 200);
  let t1;
  if ($[1] !== setDebouncedSearchTermIfTyped) {
    t1 = value_0 => {
      hasTypedSinceNavigation.current = true;
      setDebouncedSearchTermIfTyped(value_0);
    };
    $[1] = setDebouncedSearchTermIfTyped;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleChangeDebounced = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      hasTypedSinceNavigation.current = false;
      setDebouncedSearchTerm(null);
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const resetSuggestions = t2;
  const isEnabled = !!locale && !!debouncedSearchTerm && debouncedSearchTerm.length > 1;
  let t3;
  if ($[4] !== debouncedSearchTerm || $[5] !== locale) {
    t3 = ["searchSuggestions", locale, debouncedSearchTerm];
    $[4] = debouncedSearchTerm;
    $[5] = locale;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const t4 = isEnabled ? _temp2 : undefined;
  let t5;
  if ($[7] !== isEnabled || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      queryKey: t3,
      queryFn: _temp,
      staleTime: 900000,
      enabled: isEnabled,
      placeholderData: t4
    };
    $[7] = isEnabled;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    data,
    isPlaceholderData
  } = useQuery(t5);
  const suggestions = isEnabled ? data ?? EMPTY_SUGGESTIONS : EMPTY_SUGGESTIONS;
  const t6 = isPlaceholderData ? undefined : suggestions.queryCategory;
  let t7;
  if ($[11] !== debouncedSearchTerm || $[12] !== handleChangeDebounced || $[13] !== suggestions || $[14] !== t6) {
    t7 = {
      ...suggestions,
      queryCategory: t6,
      handleChangeDebounced,
      debouncedSearchTerm,
      resetSuggestions
    };
    $[11] = debouncedSearchTerm;
    $[12] = handleChangeDebounced;
    $[13] = suggestions;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
function _temp(t0) {
  const {
    queryKey: t1
  } = t0;
  const [, locale_0, term] = t1;
  return get(`/internal/api/searchSuggestions?${toQueryString({
    q: term,
    locale: locale_0
  })}`);
}
function _temp2(previousData) {
  return previousData ?? undefined;
}